import React, { useMemo, useState, useEffect } from "react"
import {
  Container,
  CardBody,
  Card,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { deleteNow } from "helpers/delete_function"
import { axiosbaseurl } from "configure"
import moment from "moment"
import Loader from "components/Common/Loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import LoadingButton from "components/Common/LoadingButton"
import swal from "sweetalert"
import Select from "react-select"
import { customStyles } from "helpers/select-styles"

const parseServiceNameId = raw => {
  try {
    if (!raw) return []
    let parsed = raw
    if (typeof parsed === "string") parsed = JSON.parse(parsed)
    if (typeof parsed === "string") parsed = JSON.parse(parsed)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const ClientEmails = () => {
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [clients, setClients] = useState([])
  const [services, setServices] = useState([])

  const [showFormModal, setShowFormModal] = useState(false)
  const [editData, setEditData] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(null)

  document.title = "Client Emails | Peacock Accountants Limited"

  useEffect(() => {
    fetchTableData()
    fetchClients()
    fetchServices()
  }, [])

  const fetchTableData = async () => {
    try {
      setLoading(true)
      const { data } = await axiosbaseurl.get("/client-email")
      if (data.success) setTableData(data.data)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      const { data } = await axiosbaseurl.get("/client")
      if (data.success)
        setClients(
          data.data.map(c => ({
            value: c.id,
            label: `${c.clientCode} - ${c.clientName}`,
            serviceNameId: c.serviceNameId,
            clientTypeId: c.clientTypeId,
          }))
        )
    } catch (err) {
      console.log(err)
    }
  }

  const fetchServices = async () => {
    try {
      const { data } = await axiosbaseurl.get("/service-master/active")
      if (data.success) setServices(data.data || [])
    } catch (err) {
      console.log(err)
    }
  }

  const handleAdd = () => {
    setEditData(null)
    setShowFormModal(true)
  }

  const handleEdit = row => {
    setEditData(row)
    setShowFormModal(true)
  }

  const handlePreview = async item => {
    try {
      setPreviewLoading(item.id)
      const url = `${process.env.REACT_APP_IMAGE_PATH}/${item.file}`
      const response = await fetch(url)
      const buffer = await response.arrayBuffer()
      const ext = item.filename?.split(".").pop()?.toLowerCase()
      let parsed = {}

      if (ext === "eml") {
        const PostalMime = (await import("postal-mime")).default
        const parser = new PostalMime()
        const email = await parser.parse(buffer)
        parsed = {
          subject: email.subject || "--",
          from: email.from?.text || email.from?.address || "--",
          to: email.to?.map(t => t.text || t.address).join(", ") || "--",
          cc: email.cc?.map(t => t.text || t.address).join(", ") || "",
          date: email.date
            ? moment(email.date).format("DD-MM-YYYY | hh:mm A")
            : "--",
          body:
            email.html ||
            (email.text
              ? `<pre style="white-space:pre-wrap;font-family:inherit">${email.text}</pre>`
              : "<p>No content</p>"),
          attachments:
            email.attachments?.map(a => ({ name: a.filename, size: a.size })) ||
            [],
        }
      } else if (ext === "msg") {
        const MsgReaderModule = await import("@kenjiuno/msgreader")
        const MsgReader = MsgReaderModule.default || MsgReaderModule

        const reader = new MsgReader(buffer)
        const fileData = reader.getFileData()

        let bodyContent =
          fileData.bodyHtml || fileData.htmlBody || fileData.body || ""

        if (!bodyContent) {
          bodyContent = "<p>No content found in MSG file</p>"
        } else if (!bodyContent.trim().startsWith("<")) {
          bodyContent = `<pre style="white-space:pre-wrap;font-family:inherit">${bodyContent}</pre>`
        }

        parsed = {
          subject: fileData.subject || "--",
          from: fileData.senderName
            ? `${fileData.senderName}${
                fileData.senderEmail ? ` <${fileData.senderEmail}>` : ""
              }`
            : fileData.senderEmail || "--",
          to:
            fileData.recipients?.map(r => r.name || r.email).join(", ") || "--",
          cc: "",
          date: fileData.messageDeliveryTime
            ? moment(fileData.messageDeliveryTime).format(
                "DD-MM-YYYY | hh:mm A"
              )
            : "--",
          body: bodyContent,
          attachments:
            fileData.attachments?.map(a => ({
              name: a.fileName || a.filename || "attachment",
            })) || [],
        }
      }

      setPreviewData(parsed)
      setShowPreview(true)
    } catch (err) {
      console.log(err)
      swal("", "Failed to preview file", "error")
    } finally {
      setPreviewLoading(null)
    }
  }

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title="Dashboard" breadcrumbItem="Mail Manager" />
          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                tableData={tableData}
                refreshData={fetchTableData}
                setTableData={setTableData}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onPreview={handlePreview}
                previewLoading={previewLoading}
                clients={clients}
                services={services}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      <AddEditModal
        show={showFormModal}
        setShow={setShowFormModal}
        editData={editData}
        setTableData={setTableData}
        refreshData={fetchTableData}
        clients={clients}
        services={services}
      />

      <PreviewModal
        show={showPreview}
        setShow={setShowPreview}
        previewData={previewData}
      />
    </>
  )
}

// ─── Table ────────────────────────────────────────────────────────────────────
const TableComponent = ({
  tableData,
  refreshData,
  onAdd,
  onEdit,
  onPreview,
  previewLoading,
  clients,
  services,
}) => {
  const navigate = useNavigate()
  const { checkAccess, permissions } = usePermissionData()
  const module_id = 33

  const [showFilterModal, setShowFilterModal] = useState(false)
  const [filterClient, setFilterClient] = useState(null)
  const [filterService, setFilterService] = useState(null)
  const [filterTask, setFilterTask] = useState(null)
  const [filterPeriodEnd, setFilterPeriodEnd] = useState("")
  const [filterDateInEmail, setFilterDateInEmail] = useState("")
  const [filterFrom, setFilterFrom] = useState(null)
  const [filterTo, setFilterTo] = useState(null)

  const activeFilterCount = [
    filterClient,
    filterService,
    filterTask,
    filterPeriodEnd,
    filterDateInEmail,
    filterFrom,
    filterTo,
  ].filter(Boolean).length

  const handleApplyFilters = ({
    client,
    service,
    task,
    periodEnd,
    dateInEmail,
    from,
    to,
  }) => {
    setFilterClient(client)
    setFilterService(service)
    setFilterTask(task)
    setFilterPeriodEnd(periodEnd)
    setFilterDateInEmail(dateInEmail)
    setFilterFrom(from)
    setFilterTo(to)
    setShowFilterModal(false)
  }

  const handleClearFilters = () => {
    setFilterClient(null)
    setFilterService(null)
    setFilterTask(null)
    setFilterPeriodEnd("")
    setFilterDateInEmail("")
    setFilterFrom(null)
    setFilterTo(null)
    setShowFilterModal(false)
  }

  const handleDelete = async id => {
    try {
      const { data } = await axiosbaseurl.delete(`/client-email/${id}`)
      if (data.success) swal("", "Record deleted successfully", "success")
      refreshData()
    } catch (err) {
      console.log(err)
      swal("", err.response?.data?.message || "Something went wrong", "error")
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        disableFilters: true,
        className: "text-center",
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      {
        Header: "Client Code",
        accessor: "client.clientCode",
        disableFilters: true,
      },
      {
        Header: "Client Name",
        accessor: "client.clientName",
        disableFilters: true,
      },
      {
        Header: "Task Name",
        accessor: "task.title",
        disableFilters: true,
        className: "",
        Cell: ({ value }) => <span>{value || "--"}</span>,
      },
      {
        Header: "Period End",
        accessor: "task.startDate",
        filterable: true,
        Cell: cellProps => (
          <span
            className={`${cellProps.row.original.isDelayed && "text-danger"}`}
          >
            {cellProps.row.original.task?.startDate
              ? moment
                  .utc(cellProps.row.original.task.startDate)
                  .format("DD/MM/YY")
              : "--"}
          </span>
        ),
      },
      {
        Header: "Service",
        accessor: "service.serviceName",
        disableFilters: true,
        className: "",
        Cell: ({ value }) => <span>{value || "--"}</span>,
      },
      //   {
      //     Header: "File Name",
      //     accessor: "filename",
      //     disableFilters: true,
      //   },
      {
        Header: "Subject",
        accessor: "subject",
        disableFilters: true,
        className: "",
        Cell: ({ value }) => (
          <span
            className="d-inline-block text-truncate"
            style={{ maxWidth: 240 }}
            title={value || ""}
          >
            {value || "--"}
          </span>
        ),
      },
      {
        Header: "Email Date",
        accessor: "dateInEmail",
        filterable: true,
        Cell: cellProps => (
          <span>
            {cellProps.row.original.dateInEmail
              ? moment
                  .utc(cellProps.row.original.dateInEmail)
                  .format("DD/MM/YY")
              : "--"}
          </span>
        ),
        filter: (rows, ids, query) => {
          return rows.filter(row => {
            const formatted = row.original.dateInEmail
              ? moment.utc(row.original.dateInEmail).format("DD/MM/YY")
              : ""
            return formatted.toLowerCase().includes(String(query).toLowerCase())
          })
        },
      },
      {
        Header: "From Email",
        accessor: "from",
        disableFilters: true,
        className: "",
        Cell: ({ value }) => <span>{value || "--"}</span>,
      },
      {
        Header: "To Email",
        accessor: "to",
        disableFilters: true,
        className: "",
        Cell: ({ value }) => <span>{value || "--"}</span>,
      },
      //   {
      //     Header: "Uploaded At",
      //     accessor: "createdAt",
      //     disableFilters: true,
      //     className: "text-center text-nowrap",
      //     Cell: ({ value }) => (
      //       <span>{moment(value).format("DD MMM YYYY | hh:mm A")}</span>
      //     ),
      //   },
      {
        Header: "Action",
        disableFilters: true,
        disableSort: true,
        accessor: "view",
        className: "text-center",
        Cell: ({ row }) => {
          const item = row.original
          return (
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-sm btn-info"
                title="Preview Email"
                disabled={previewLoading === item.id}
                onClick={() => onPreview(item)}
              >
                <i
                  className={
                    previewLoading === item.id
                      ? "bx bx-loader bx-spin"
                      : "fas fa-eye"
                  }
                />
              </button>
              <a
                href={`${process.env.REACT_APP_IMAGE_PATH}/${item.file}`}
                target="_blank"
                rel="noreferrer"
                download
                className="btn btn-sm btn-primary"
                title="Download"
              >
                <i className="fas fa-download" />
              </a>
              {checkAccess(module_id, "edit") && (
                <button
                  onClick={() => onEdit(item)}
                  className="btn btn-sm btn-success bg-green"
                  title="Edit"
                >
                  <i className="fas fa-pen" />
                </button>
              )}
              {checkAccess(module_id, "remove") && (
                <button
                  onClick={() => deleteNow(() => handleDelete(item.id))}
                  className="btn btn-sm btn-danger"
                  title="Delete"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [permissions, previewLoading]
  )

  const data = useMemo(() => {
    let rows = tableData
    if (filterClient) rows = rows.filter(r => r.clientId === filterClient.value)
    if (filterService)
      rows = rows.filter(r => r.serviceId === filterService.value)
    if (filterTask) rows = rows.filter(r => r.taskId === filterTask.value)
    if (filterPeriodEnd)
      rows = rows.filter(
        r =>
          r.task?.startDate &&
          moment.utc(r.task.startDate).format("YYYY-MM-DD") === filterPeriodEnd
      )
    if (filterDateInEmail)
      rows = rows.filter(r => r.dateInEmail === filterDateInEmail)
    if (filterFrom)
      rows = rows.filter(
        r => r.from && r.from.toLowerCase() === filterFrom.value.toLowerCase()
      )
    if (filterTo)
      rows = rows.filter(
        r => r.to && r.to.toLowerCase() === filterTo.value.toLowerCase()
      )
    return rows
  }, [
    tableData,
    filterClient,
    filterService,
    filterTask,
    filterPeriodEnd,
    filterDateInEmail,
    filterFrom,
    filterTo,
  ])

  return (
    <PermittedComponent isAccess={checkAccess(module_id, "view")}>
      <CommonTable columns={columns} data={data} heading="Client Emails">
        <div>
          <button
            onClick={() => setShowFilterModal(true)}
            className={`btn ms-3 ${
              activeFilterCount ? "btn-warning" : "btn-outline-secondary"
            }`}
          >
            <i className="fas fa-filter me-2" />
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-danger ms-2">{activeFilterCount}</span>
            )}
          </button>
          {checkAccess(module_id, "add") && (
            <button onClick={onAdd} className="btn btn-primary bg-blue ms-3">
              <i className="fas fa-plus me-2" />
              Add Client Email
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary bg-dark ms-3"
          >
            <i className="fas fa-arrow-left me-3" />
            Back
          </button>
        </div>
      </CommonTable>

      <FilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        clients={clients}
        tableData={tableData}
        applied={{
          client: filterClient,
          service: filterService,
          task: filterTask,
          periodEnd: filterPeriodEnd,
          dateInEmail: filterDateInEmail,
          from: filterFrom,
          to: filterTo,
        }}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />
    </PermittedComponent>
  )
}

// ─── Filter Modal ─────────────────────────────────────────────────────────────
const FilterModal = ({
  show,
  setShow,
  clients,
  tableData,
  applied,
  onApply,
  onClear,
}) => {
  const [draftClient, setDraftClient] = useState(null)
  const [draftService, setDraftService] = useState(null)
  const [draftTask, setDraftTask] = useState(null)
  const [draftPeriodEnd, setDraftPeriodEnd] = useState("")
  const [draftDateInEmail, setDraftDateInEmail] = useState("")
  const [draftFrom, setDraftFrom] = useState(null)
  const [draftTo, setDraftTo] = useState(null)

  // Sync draft from applied filters when modal opens
  useEffect(() => {
    if (show) {
      setDraftClient(applied.client)
      setDraftService(applied.service)
      setDraftTask(applied.task)
      setDraftPeriodEnd(applied.periodEnd)
      setDraftDateInEmail(applied.dateInEmail)
      setDraftFrom(applied.from)
      setDraftTo(applied.to)
    }
  }, [show])

  const serviceOptions = useMemo(() => {
    const rows = draftClient
      ? tableData.filter(r => r.clientId === draftClient.value)
      : tableData
    const seen = new Map()
    rows.forEach(r => {
      if (r.serviceId && r.service?.serviceName)
        seen.set(r.serviceId, r.service.serviceName)
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [tableData, draftClient])

  const taskOptions = useMemo(() => {
    let rows = tableData
    if (draftClient) rows = rows.filter(r => r.clientId === draftClient.value)
    if (draftService)
      rows = rows.filter(r => r.serviceId === draftService.value)
    const seen = new Map()
    rows.forEach(r => {
      if (r.taskId && r.task?.title) seen.set(r.taskId, r.task.title)
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [tableData, draftClient, draftService])

  const clientOptions = useMemo(() => {
    const seen = new Set()
    tableData.forEach(r => {
      if (r.clientId) seen.add(r.clientId)
    })
    return clients.filter(c => seen.has(c.value))
  }, [clients, tableData])

  const fromOptions = useMemo(() => {
    const seen = new Map()
    tableData.forEach(r => {
      if (r.from) seen.set(r.from, r.from)
    })
    return Array.from(seen, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    )
  }, [tableData])

  const toOptions = useMemo(() => {
    const seen = new Map()
    tableData.forEach(r => {
      if (r.to) seen.set(r.to, r.to)
    })
    return Array.from(seen, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    )
  }, [tableData])

  const handleClientChange = selected => {
    setDraftClient(selected)
    setDraftService(null)
    setDraftTask(null)
  }

  const handleServiceChange = selected => {
    setDraftService(selected)
    setDraftTask(null)
  }

  return (
    <Modal size="lg" isOpen={show} toggle={() => setShow(false)} centered>
      <div className="modal-content">
        <ModalHeader className="bg-primary">
          <div className="d-flex align-items-center justify-content-between w-100 px-2">
            <h5 className="mb-0 text-white">
              <i className="fas fa-filter me-2" />
              Filter Emails
            </h5>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => setShow(false)}
            >
              <i className="fa fa-times me-2" />
              Close
            </button>
          </div>
        </ModalHeader>

        <ModalBody className="px-4 py-4">
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Client</label>
              <Select
                value={draftClient}
                onChange={handleClientChange}
                options={clientOptions}
                placeholder="All Clients"
                isClearable
                styles={customStyles}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Service</label>
              <Select
                value={draftService}
                onChange={handleServiceChange}
                options={serviceOptions}
                placeholder={
                  serviceOptions.length
                    ? "All Services"
                    : "No services available"
                }
                isDisabled={!serviceOptions.length}
                isClearable
                styles={customStyles}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Task</label>
              <Select
                value={draftTask}
                onChange={setDraftTask}
                options={taskOptions}
                placeholder={
                  taskOptions.length ? "All Tasks" : "No tasks available"
                }
                isDisabled={!taskOptions.length}
                isClearable
                styles={customStyles}
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Period End</label>
              <input
                type="date"
                className="form-control input-field"
                value={draftPeriodEnd}
                onChange={e => setDraftPeriodEnd(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Date in Email</label>
              <input
                type="date"
                className="form-control input-field"
                value={draftDateInEmail}
                onChange={e => setDraftDateInEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">From</label>
              <Select
                value={draftFrom}
                onChange={setDraftFrom}
                options={fromOptions}
                placeholder="All Senders"
                isClearable
                styles={customStyles}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">To</label>
              <Select
                value={draftTo}
                onChange={setDraftTo}
                options={toOptions}
                placeholder="All Recipients"
                isClearable
                styles={customStyles}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClear}
            >
              <i className="fas fa-times me-2" />
              Clear Filters
            </button>
            <button
              type="button"
              className="btn btn-primary bg-blue"
              onClick={() =>
                onApply({
                  client: draftClient,
                  service: draftService,
                  task: draftTask,
                  periodEnd: draftPeriodEnd,
                  dateInEmail: draftDateInEmail,
                  from: draftFrom,
                  to: draftTo,
                })
              }
            >
              <i className="fas fa-check me-2" />
              Apply Filters
            </button>
          </div>
        </ModalBody>
      </div>
    </Modal>
  )
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────
const defaultForm = {
  clientId: null,
  serviceId: null,
  taskId: null,
  label: "",
  file: null,
}

const AddEditModal = ({
  show,
  setShow,
  editData,
  refreshData,
  setTableData,
  clients,
  services,
}) => {
  const isEdit = Boolean(editData)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(defaultForm)

  // Task dropdown is fetched dynamically based on client + service
  const [availableServices, setAvailableServices] = useState([])
  const [availableTasks, setAvailableTasks] = useState([])
  const [tasksLoading, setTasksLoading] = useState(false)

  // Populate form + derived dropdowns whenever the modal opens
  useEffect(() => {
    if (!show) return

    if (editData) {
      // 1. Resolve the client option
      const clientOption =
        clients.find(c => c.value === editData.clientId) || null

      const clientServices = clientOption
        ? parseServiceNameId(clientOption.serviceNameId).map(s => ({
            value: s.id,
            label: s.name,
          }))
        : []

      const restServices = clientOption
        ? services
            .filter(
              s =>
                s.status === "Active" &&
                (s.clientTypeId === clientOption.clientTypeId ||
                  s.clientTypeId === 0) &&
                s.serviceCategory !== "Billiable and scheduled" &&
                s.serviceCategory !== "Billiable and Non-scheduled"
            )
            .map(s => ({
              value: s.id,
              label: s.serviceName,
            }))
        : []

      setAvailableServices([...clientServices, ...restServices])

      // 2. Fetch tasks if both ids already exist
      if (editData.clientId && editData.serviceId) {
        fetchTasksForSelection(editData.clientId, editData.serviceId)
      } else {
        setAvailableTasks([])
      }

      // 3. Fill form — service comes from client's selected services
      setForm({
        clientId: clientOption,
        serviceId: editData.serviceId
          ? clientServices.find(s => s.value === editData.serviceId) || {
              value: editData.serviceId,
              label: editData?.serviceName || editData.service?.serviceName,
            }
          : null,
        taskId: editData.taskId
          ? { value: editData.taskId, label: editData.task?.title }
          : null,
        label: editData.label || "",
        file: null,
      })
    } else {
      setForm(defaultForm)
      setAvailableServices([])
      setAvailableTasks([])
    }
  }, [show, editData, clients, services])

  // Fetch tasks filtered by clientId + serviceId
  const fetchTasksForSelection = async (clientId, serviceId) => {
    try {
      setTasksLoading(true)
      const { data } = await axiosbaseurl.get(
        `/tasks/all/normal?clientId=${clientId}&serviceId=${serviceId}`
      )
      if (data.success)
        setAvailableTasks(
          data.data.map(t => ({
            value: t.id,
            label: `${t.title} (${
              t.startDate ? moment(t.startDate).format("MM/DD/YYYY") : ""
            })`,
          }))
        )
      else setAvailableTasks([])
    } catch (err) {
      console.log(err)
      setAvailableTasks([])
    } finally {
      setTasksLoading(false)
    }
  }

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  // Client selected → reset service & task
  const handleClientChange = selected => {
    set("clientId", selected)
    set("serviceId", null)
    set("taskId", null)
    setAvailableTasks([])
    if (selected) {
      const clientServices = parseServiceNameId(selected.serviceNameId).map(
        s => ({
          value: s.id,
          label: s.name,
        })
      )
      const restServices = selected
        ? services
            .filter(
              s =>
                s.serviceCategory !== "Billiable and scheduled" &&
                s.serviceCategory !== "Billiable and Non-scheduled" &&
                (s.clientTypeId === selected.clientTypeId ||
                  s.clientTypeId === 0)
            )
            .map(s => ({
              value: s.id,
              label: s.serviceName,
            }))
        : []
      setAvailableServices([...clientServices, ...restServices])
    } else {
      setAvailableServices([])
    }
  }

  // Service selected → fetch tasks for clientId + serviceId
  const handleServiceChange = selected => {
    set("serviceId", selected)
    set("taskId", null)
    setAvailableTasks([])

    if (selected && form.clientId) {
      fetchTasksForSelection(form.clientId.value, selected.value)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!form.clientId) {
      swal("", "Please select a client", "warning")
      return
    }
    try {
      setLoading(true)
      const fd = new FormData()
      fd.append("clientId", form.clientId.value)
      if (form.serviceId) fd.append("serviceId", form.serviceId.value)
      if (form.taskId) fd.append("taskId", form.taskId.value)
      if (form.label) fd.append("label", form.label)
      if (form.file) fd.append("file", form.file)

      const { data } = isEdit
        ? await axiosbaseurl.put(`/client-email/${editData.id}`, fd)
        : await axiosbaseurl.post("/client-email", fd)

      if (data.success) {
        swal(
          "",
          isEdit ? "Record updated successfully" : "Record added successfully",
          "success"
        )
        if (isEdit) {
          refreshData()
        } else {
          setTableData(prev => [...prev, data.data])
        }
        setShow(false)
      }
    } catch (err) {
      console.log(err)
      swal("", err?.response?.data?.message || "Something went wrong", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal size="lg" isOpen={show} toggle={() => setShow(false)} centered>
      <div className="modal-content">
        <form onSubmit={handleSubmit}>
          <ModalHeader className="bg-primary">
            <div className="d-flex align-items-center justify-content-between w-100 px-2">
              <h5 className="mb-0 text-white">
                {isEdit ? "Edit Client Email" : "Add Client Email"}
              </h5>
              <div>
                <LoadingButton
                  loading={loading}
                  btnText={isEdit ? "Update" : "Save"}
                  btn="light"
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm ms-3"
                  onClick={() => setShow(false)}
                >
                  <i className="fa fa-times me-2" />
                  Close
                </button>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="px-4 py-4">
            <div className="row">
              {/* Client ─ required */}
              <div className="col-md-6 mb-3">
                <label>
                  Client <span className="text-danger">*</span>
                </label>
                <Select
                  value={form.clientId}
                  onChange={handleClientChange}
                  options={clients}
                  placeholder="Select Client"
                  isClearable
                  styles={customStyles}
                />
              </div>

              {/* Service ─ enabled after client selection, sourced from client's services */}
              <div className="col-md-6 mb-3">
                <label>Service</label>
                <Select
                  value={form.serviceId}
                  onChange={handleServiceChange}
                  options={availableServices}
                  placeholder={
                    !form.clientId
                      ? "Select a client first"
                      : availableServices.length
                      ? "Select Service (optional)"
                      : "No services for this client"
                  }
                  isDisabled={!form.clientId || !availableServices.length}
                  isClearable
                  styles={customStyles}
                />
              </div>

              {/* Task ─ unlocked after service is selected; loaded via API */}
              <div className="col-md-6 mb-3">
                <label>Task</label>
                <Select
                  value={form.taskId}
                  onChange={val => set("taskId", val)}
                  options={availableTasks}
                  placeholder={
                    !form.serviceId
                      ? "Select a service first"
                      : tasksLoading
                      ? "Loading tasks…"
                      : availableTasks.length
                      ? "Select Task (optional)"
                      : "No tasks found for this client & service"
                  }
                  isDisabled={!form.serviceId || tasksLoading}
                  isLoading={tasksLoading}
                  isClearable
                  styles={customStyles}
                />
              </div>

              {/* Label */}
              <div className="col-md-6 mb-3">
                <label>Label</label>
                <input
                  type="text"
                  className="form-control input-field"
                  placeholder="Optional description"
                  value={form.label}
                  onChange={e => set("label", e.target.value)}
                />
              </div>

              {/* File ─ required on Add, optional on Edit */}
              <div className="col-12 mb-2">
                <label>
                  File {!isEdit && <span className="text-danger">*</span>}
                </label>
                <input
                  type="file"
                  accept=".msg,.eml"
                  className="form-control input-field"
                  onChange={e => set("file", e.target.files[0] || null)}
                  required={!isEdit}
                />
                <small className="text-muted">
                  Accepted formats: .msg, .eml
                </small>
                {isEdit && editData?.filename && (
                  <small className="d-block text-info mt-1">
                    <i className="fas fa-paperclip me-1" />
                    Current file: <strong>{editData.filename}</strong>
                    {" — leave blank to keep existing"}
                  </small>
                )}
              </div>
            </div>
          </ModalBody>
        </form>
      </div>
    </Modal>
  )
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
const PreviewModal = ({ show, setShow, previewData }) => (
  <Modal size="xl" isOpen={show} toggle={() => setShow(false)} centered>
    <div className="modal-content">
      <ModalHeader className="bg-primary">
        <div className="d-flex align-items-center justify-content-between w-100 px-2">
          <h5 className="mb-0 text-white">Email Preview</h5>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => setShow(false)}
          >
            <i className="fa fa-times me-2" />
            Close
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="px-4 py-3">
        {previewData ? (
          <div>
            <table className="table table-bordered mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 100 }} className="table-light">
                    Subject
                  </th>
                  <td>{previewData.subject}</td>
                </tr>
                <tr>
                  <th className="table-light">From</th>
                  <td>{previewData.from}</td>
                </tr>
                <tr>
                  <th className="table-light">To</th>
                  <td>{previewData.to}</td>
                </tr>
                {previewData.cc && (
                  <tr>
                    <th className="table-light">CC</th>
                    <td>{previewData.cc}</td>
                  </tr>
                )}
                <tr>
                  <th className="table-light">Date</th>
                  <td>{previewData.date}</td>
                </tr>
              </tbody>
            </table>

            <div
              className="border rounded p-3 bg-light mb-4"
              style={{ minHeight: 200, maxHeight: 500, overflowY: "auto" }}
            >
              <div dangerouslySetInnerHTML={{ __html: previewData.body }} />
            </div>

            {previewData.attachments.length > 0 && (
              <div>
                <h6 className="fw-semibold mb-2">
                  <i className="fas fa-paperclip me-2" />
                  Attachments ({previewData.attachments.length})
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {previewData.attachments.map((att, i) => (
                    <span key={i} className="badge bg-secondary py-2 px-3 fs-6">
                      <i className="fas fa-file me-2" />
                      {att.name}
                      {att.size ? ` (${(att.size / 1024).toFixed(1)} KB)` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            No preview available
          </div>
        )}
      </ModalBody>
    </div>
  </Modal>
)

export default ClientEmails
